"use client"

import { createContext, useContext, useState, type ReactNode } from "react"

export interface Task {
  id: string
  title: string
  description: string
  priority: "low" | "medium" | "high"
  dueDate: string
  candidateId?: string
  candidateName?: string
  jobId?: string
  jobTitle?: string
  assignee: string
  status: "todo" | "inProgress" | "blocked" | "done"
  createdAt: string
}

export interface Comment {
  id: string
  content: string
  type:
    | "note"
    | "call"
  candidateId?: string
  candidateName?: string
  jobId?: string
  jobTitle?: string
  author: string
  createdAt: string
}

interface TasksCommentsContextType {
  tasks: Task[]
  comments: Comment[]
  addTask: (task: Omit<Task, "id" | "createdAt">) => void
  addComment: (comment: Omit<Comment, "id" | "createdAt">) => void
  updateTask: (taskId: string, updates: Partial<Omit<Task, "id" | "createdAt">>) => void
  updateComment: (commentId: string, updates: Partial<Omit<Comment, "id" | "createdAt">>) => void
  getTasksForCandidate: (candidateId: string) => Task[]
  getCommentsForCandidate: (candidateId: string) => Comment[]
  getAllTasks: () => Task[]
  updateTaskStatus: (taskId: string, status: Task["status"]) => void
}

const TasksCommentsContext = createContext<TasksCommentsContextType | undefined>(undefined)

export function TasksCommentsProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([
    {
      id: "1",
      title: "Follow up with Sarah Chen",
      description: "Send follow-up email about Python Developer role",
      priority: "high",
      dueDate: "2024-01-16",
      candidateId: "644525821",
      candidateName: "Sarah Chen",
      jobId: "1",
      jobTitle: "Senior Python Developer",
      assignee: "You",
      status: "todo",
      createdAt: "2024-01-15T10:00:00Z",
    },
  ])

  const [comments, setComments] = useState<Comment[]>([
    {
      id: "1",
      content: "Great technical skills, strong Python background. Recommended for next round.",
      type: "note",
      candidateId: "644525821",
      candidateName: "Sarah Chen",
      jobId: "1",
      jobTitle: "Senior Python Developer",
      author: "You",
      createdAt: "2024-01-15T09:30:00Z",
    },
  ])

  const addTask = (taskData: Omit<Task, "id" | "createdAt">) => {
    const newTask: Task = {
      ...taskData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    }
    setTasks((prev) => [...prev, newTask])
  }

  const addComment = (commentData: Omit<Comment, "id" | "createdAt">) => {
    const newComment: Comment = {
      ...commentData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    }
    setComments((prev) => [...prev, newComment])
  }

  const updateTask = (taskId: string, updates: Partial<Omit<Task, "id" | "createdAt">>) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, ...updates } : task)))
  }

  const updateComment = (commentId: string, updates: Partial<Omit<Comment, "id" | "createdAt">>) => {
    setComments((prev) => prev.map((comment) => (comment.id === commentId ? { ...comment, ...updates } : comment)))
  }

  const getTasksForCandidate = (candidateId: string) => {
    return tasks.filter((task) => task.candidateId === candidateId)
  }

  const getCommentsForCandidate = (candidateId: string) => {
    return comments.filter((comment) => comment.candidateId === candidateId)
  }

  const getAllTasks = () => {
    return tasks
  }

  const updateTaskStatus = (taskId: string, status: Task["status"]) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, status } : task)))
  }

  return (
    <TasksCommentsContext.Provider
      value={{
        tasks,
        comments,
        addTask,
        addComment,
        updateTask,
        updateComment,
        getTasksForCandidate,
        getCommentsForCandidate,
        getAllTasks,
        updateTaskStatus,
      }}
    >
      {children}
    </TasksCommentsContext.Provider>
  )
}

export function useTasksComments() {
  const context = useContext(TasksCommentsContext)
  if (context === undefined) {
    throw new Error("useTasksComments must be used within a TasksCommentsProvider")
  }
  return context
}
